import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { FaUser, FaEnvelope, FaPhone, FaCalendarAlt, FaUsers } from 'react-icons/fa';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { saveEnquiry } from '../functions/Enquiry';

const EnquiryPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    travelDate: '',
    groupSize: '',
    travelType: 'group', // Default to group travel
    message: '',
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [bannerImage, setBannerImage] = useState('');
  const formRef = useRef(null); // Add ref for hidden form

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    async function fetchBannerImage() {
      try {
        const docRef = doc(db, "Images", "AllImages");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists() && docSnap.data()["Enquiry Page"] && docSnap.data()["Enquiry Page"].url) {
          setBannerImage(docSnap.data()["Enquiry Page"].url);
        }
      } catch (error) {
        // fallback: do nothing
      }
    }
    fetchBannerImage();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null,
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email address is invalid';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    // Removed validation for message field to make it optional

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);

    try {
      // Prepare data for Firestore
      const enquiryData = {
        contactInfo: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
        },
        travelDate: formData.travelDate,
        groupSize: formData.groupSize,
        travelType: formData.travelType,
        message: formData.message,
      };

      await saveEnquiry(enquiryData);

      // Submit to formsubmit.co via hidden form
      if (formRef.current) {
        formRef.current.submit();
      }

      setFormData({
        name: '',
        email: '',
        phone: '',
        travelDate: '',
        groupSize: '',
        travelType: 'group',
        message: '',
      });

      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 5000);
    } catch (error) {
      // console.error('Error submitting form:', error);
      setErrors({ submit: 'Failed to submit the form. Please try again later.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="enquiry-page py-5"
      style={{
        backgroundImage:
          `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.7)), url('${bannerImage || "https://images.unsplash.com/photo-1589883661923-6476cb0ae9f2?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80"}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        color: 'white',
      }}
    >
      <Container>
        {/* Hidden form for formsubmit.co */}
        <form
          ref={formRef}
          action="https://formsubmit.co/cse.officialwasim@gmail.com"
          method="POST"
          target="hidden-formsubmit-frame"
          style={{ display: 'none' }}
        >
          <input type="hidden" name="_template" value="table" />
          <input type="hidden" name="Name" value={formData.name} />
          <input type="hidden" name="Email" value={formData.email} />
          <input type="hidden" name="Phone" value={formData.phone} />
          <input type="hidden" name="Travel Date" value={formData.travelDate} />
          <input type="hidden" name="Group Size" value={formData.groupSize} />
          <input type="hidden" name="Travel Type" value={formData.travelType} />
          <input type="hidden" name="Message" value={formData.message} />
          <input type="hidden" name="_captcha" value="false" />
        </form>
        {/* Hidden iframe for formsubmit.co */}
        <iframe
          name="hidden-formsubmit-frame"
          style={{ display: 'none' }}
          title="hidden-formsubmit-frame"
        />
        <Row className="justify-content-center mb-5">
          <Col md={10} lg={8}>
            <div className="text-center mb-5">
              <h1
                className="fw-bold text-white"
                style={{
                  fontSize: isMobile ? '2rem' : '3.5rem',
                  lineHeight: isMobile ? '1.2' : '1.4',
                }}
              >
                Sway Our Way Ladakh Trip
              </h1>
              <p
                className="lead text-white"
                style={{
                  fontSize: isMobile ? '1rem' : '1.25rem',
                  marginTop: isMobile ? '0.75rem' : '1.5rem',
                }}
              >
                Kindly share your contact number to get details of our exclusive Ladakh trips.
                Our travel experts will reach out to help you plan your dream Ladakh adventure.
              </p>
            </div>

            {submitted && (
              <Alert 
                variant="warning" 
                className="mb-4"
                style={{
                  backgroundColor: '#FFA500',
                  borderColor: '#FF8C00',
                  color: 'white',
                  fontWeight: '500'
                }}
              >
                Thank you for your interest in our Ladakh trips! We'll contact you shortly with exciting details.
              </Alert>
            )}

            {errors.submit && (
              <Alert variant="danger" className="mb-4">
                {errors.submit}
              </Alert>
            )}

            <div className="bg-white p-4 p-md-5 rounded shadow" 
              style={{ 
                color: '#333', 
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                borderRadius: '12px'
              }}>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label
                        className="fw-bold"
                        style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}
                      >
                        <FaUser className="me-2" />
                        Your Name*
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        isInvalid={!!errors.name}
                        placeholder="Enter your full name"
                        style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.name}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label
                        className="fw-bold"
                        style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}
                      >
                        <FaPhone className="me-2" />
                        Whatsapp Number*
                      </Form.Label>
                      <Form.Control
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        isInvalid={!!errors.phone}
                        placeholder="Enter your phone number"
                        style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.phone}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label
                        className="fw-bold"
                        style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}
                      >
                        <FaEnvelope className="me-2" />
                        Email Address*
                      </Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        isInvalid={!!errors.email}
                        placeholder="Enter your email"
                        style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.email}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label
                        className="fw-bold"
                        style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}
                      >
                        <FaCalendarAlt className="me-2" />
                        Any Tentative/Planned Travel Date?
                      </Form.Label>
                      <div className="position-relative">
                        <Form.Control
                          type="date"
                          name="travelDate"
                          value={formData.travelDate}
                          onChange={handleChange}
                          style={{ 
                            fontSize: isMobile ? '0.9rem' : '1rem',
                            cursor: 'pointer',
                            caretColor: 'transparent'
                          }}
                          onClick={(e) => {
                            // Force open the date picker when clicking anywhere on the input
                            e.currentTarget.showPicker();
                          }}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label
                        className="fw-bold"
                        style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}
                      >
                        <FaUsers className="me-2" />
                        Group Size
                      </Form.Label>
                      <Form.Select
                        name="groupSize"
                        value={formData.groupSize}
                        onChange={handleChange}
                        style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}
                      >
                        <option value="">Select group size</option>
                        <option value="1">Solo Traveler</option>
                        <option value="couple">Couple</option>
                        <option value="2">2 People</option>
                        <option value="3-5">3-5 People</option>
                        <option value="6-10">6-10 People</option>
                        <option value="10+">More than 10 People</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label
                        className="fw-bold"
                        style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}
                      >
                        Type of Travel
                      </Form.Label>
                      <Form.Select
                        name="travelType"
                        value={formData.travelType}
                        onChange={handleChange}
                        style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}
                      >
                        <option value="group">Group/Fixed Departure</option>
                        <option value="private">Private Customized Trip</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-4">
                  <Form.Label
                    className="fw-bold"
                    style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}
                  >
                    Your Enquiry
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    isInvalid={!!errors.message}
                    rows={5}
                    placeholder="Please tell us about your travel plans and any specific requirements..."
                    style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <div className="d-grid">
                  <Button
                    variant="primary"
                    type="submit"
                    size="lg"
                    disabled={submitting}
                    style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}
                  >
                    {submitting ? 'Submitting...' : 'Submit Enquiry'}
                  </Button>
                </div>
              </Form>
            </div>

            <div className="mt-5 text-center text-white">
              <h3 style={{ fontSize: isMobile ? '1.5rem' : '1.75rem' }}>
                Direct Contact for Ladakh Trips
              </h3>
              <p className="mb-1" style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>
                Phone: +91 92204 24206
              </p>
              <p className="mb-1" style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>
                Email: infoswayourway@gmail.com 
              </p>
              <p style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>
                WhatsApp: +91 92204 24206
              </p>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default EnquiryPage;
